import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useDbStore } from './dbStore'
import { useAppStore } from './app'
import { translateCropName } from '../i18n/cropVocab'
import { api, BASE_URL } from '../services/api.js'
import { enrichPlotWithOpenData, isOutdated, getVersionDifference } from '../services/enrichmentService.js'
import i18n from '@/i18n'

const t = (key, params) => i18n.global.t(`poderiStore.${key}`, params)

export const usePoderiStore = defineStore('poderi', () => {
    const dbStore = useDbStore()

    // State
    const currentFarmCenter = ref(null)
    const currentSeasonId = ref(null)   // stagione selezionata corrente, condivisa tra views
    const operations = ref([])
    const observations = ref([])
    const allFields = ref([])
    const products = ref([])
    const loading = ref(false)
    const error = ref(null)

    // Actions

    async function fetchFarmCenters() {
        const result = await dbStore.select('farm_center')
        if (result.ok) {
            return result.data || []
        }
        throw new Error(result.message || 'Errore caricamento farm centers')
    }

    async function fetchFarmCenterById(id) {
        loading.value = true
        error.value = null
        try {
            const result = await dbStore.selectOne('farm_center', id)
            if (result.ok) {
                currentFarmCenter.value = result.data
            } else {
                error.value = result.message || 'Azienda non trovata'
            }
        } catch (e) {
            error.value = e.message
        } finally {
            loading.value = false
        }
    }

    async function fetchPlotsByFarmCenter(farmCenterId) {
        const result = await dbStore.select('plot', { id_farm_center: farmCenterId })
        return result?.ok ? result.data || [] : []
    }

    async function fetchSoils(farmCenterId) {
        const result = await dbStore.select('soil', { id_farm_center: farmCenterId })
        return result?.ok ? result.data || [] : []
    }

    /**
     * Trova il suolo associato a un plot/field
     * @param {string|number} plotId - ID del plot
     * @param {string|number} farmCenterId - ID del farm center
     * @returns {Promise<Object|null>} - Suolo trovato o null
     */
    async function getSoil(plotId, farmCenterId) {
        if (!farmCenterId) return null
              
        // Carica tutti i suoli dell'azienda
        const soils = await fetchSoils(farmCenterId)
        if (!soils || soils.length === 0) return null
        
        // Cerca un suolo specificamente associato al plot
        if (plotId) {
            const soilForPlot = soils.find(soil => {
                if (!soil.id_plots) return false
                const plotIds = typeof soil.id_plots === 'string' 
                    ? JSON.parse(soil.id_plots) 
                    : soil.id_plots
                return plotIds.includes(plotId)
            })
            
            if (soilForPlot) return soilForPlot
        }
        
        // Se non trovato, usa il suolo default (is_default === 1)
        const defaultSoil = soils.find(soil => soil.is_default === 1 || soil.is_default === true)
        return defaultSoil || null
    }

    async function fetchSeasonsByFarmCenter(farmCenterId) {
        const result = await dbStore.select('season', { id_farm_center: farmCenterId })
        return result?.ok ? result.data || [] : []
    }

    async function fetchAllPlots() {
        const result = await dbStore.select('plot')
        return result?.ok ? result.data || [] : []
    }

    async function fetchAllSeasons() {
        const result = await dbStore.select('season')
        return result?.ok ? result.data || [] : []
    }

    async function createFarmCenter(data) {
        try {
            const result = await dbStore.insert('farm_center', data)
            if (result.ok) {
                return { ok: true, data: result.data }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function updateFarmCenter(id, changes) {
        try {
            const result = await dbStore.update('farm_center', id, changes)
            if (result.ok) {
                if (currentFarmCenter.value?.id_farm_center === id) {
                    currentFarmCenter.value = { ...currentFarmCenter.value, ...changes }
                }
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function createSoil(data) {
        try {
            const result = await dbStore.insert('soil', data)
            
            // Se è default, rimuovi il flag default dalle altre analisi dello stesso farm_center
            if (result.ok && data.is_default) {
                const otherSoils = await dbStore.select('soil', { id_farm_center: data.id_farm_center })
                if (otherSoils.ok && otherSoils.data) {
                    const newSoilId = result.data // insert ritorna la chiave
                    for (const soil of otherSoils.data) {
                        if (soil.id_soil !== newSoilId && soil.is_default) {
                            await dbStore.update('soil', soil.id_soil, { is_default: 0 })
                        }
                    }
                }
            }
            
            return result
        } catch (e) {
            console.error('Errore create soil:', e)
            return { ok: false, message: e.message }
        }
    }

    async function updateSoil(id, changes) {
        try {
            const result = await dbStore.update('soil', id, changes)
            
            // Se è default, rimuovi il flag default dalle altre analisi dello stesso farm_center
            if (result.ok && changes.is_default) {
                // Serve farm_center_id per trovare le altre - lo prendiamo dal record esistente
                const currentSoil = await dbStore.selectOne('soil', id)
                if (currentSoil.ok && currentSoil.data) {
                    const otherSoils = await dbStore.select('soil', { id_farm_center: currentSoil.data.id_farm_center })
                    if (otherSoils.ok && otherSoils.data) {
                        for (const soil of otherSoils.data) {
                            if (soil.id_soil !== id && soil.is_default) {
                                await dbStore.update('soil', soil.id_soil, { is_default: 0 })
                            }
                        }
                    }
                }
            }
            
            return result
        } catch (e) {
            console.error('Errore update soil:', e)
            return { ok: false, message: e.message }
        }
    }

    async function deleteSoil(soilId) {
        try {
            return await dbStore.delete('soil', soilId)
        } catch (e) {
            console.error('Errore eliminazione soil:', e)
            return { ok: false, message: e.message }
        }
    }

    /**
     * Arricchisce un plot con dati open data
     * @param {string} plotId - ID del plot
     * @param {boolean} partialUpdate - Se true, interroga solo i nuovi criteri
     */
    async function enrichPlot(plotId, partialUpdate = false) {
        const plotResult = await dbStore.selectOne('plot', plotId)
        if (!plotResult.ok || !plotResult.data?.geojson) {
            return { ok: false, message: t('plotNotFoundOrNoGeometry') }
        }
        
        try {
            const existingData = plotResult.data.open_data 
                ? (typeof plotResult.data.open_data === 'string' 
                    ? JSON.parse(plotResult.data.open_data) 
                    : plotResult.data.open_data)
                : null
            
            const results = await enrichPlotWithOpenData(
                plotResult.data.geojson,
                partialUpdate,
                existingData
            )
            
            // Salva con versione
            await dbStore.update('plot', plotId, {
                open_data: JSON.stringify(results),
                enriched_at: results.enriched_at
            })
            
            return { ok: true, data: results }
        } catch (error) {
            console.error('Errore enrichment plot:', error)
            return { ok: false, message: error.message }
        }
    }

    /**
     * Verifica quali plot hanno dati obsoleti
     */
    async function checkPlotsNeedingUpdate(farmCenterId) {
        const plots = await fetchPlotsByFarmCenter(farmCenterId)
        
        const outdatedPlots = plots.filter(plot => {
            if (!plot.open_data) return false
            const data = typeof plot.open_data === 'string' ? JSON.parse(plot.open_data) : plot.open_data
            return isOutdated(data)
        })
        
        return outdatedPlots
    }

    /**
     * Ottiene i dati di enrichment di un plot
     */
    function getPlotEnrichmentData(plot) {
        if (!plot?.open_data) return null
        
        try {
            const data = typeof plot.open_data === 'string' ? JSON.parse(plot.open_data) : plot.open_data
            return {
                ...data,
                isOutdated: isOutdated(data),
                versionDiff: data.version ? getVersionDifference(data.version) : null
            }
        } catch (error) {
            console.error('Errore parsing open_data:', error)
            return null
        }
    }

    // Owner dell'azienda, calcolato offline dalla tabella team locale.
    // Nessuna riga team per l'azienda = appena creata in locale (la riga owner la crea il server) → owner.
    function _isOwnerFromTeamRows(rows, uid) {
        if (rows.length === 0) return true
        const mine = rows.find(r => String(r.uid) === uid)
        return !!mine && mine.is_owner === true
    }

    async function isFarmCenterOwner(id) {
        const res = await dbStore.select('team', { id_farm_center: id })
        if (!res?.ok) return true // tabella team non disponibile: decide il server in /sync
        return _isOwnerFromTeamRows(res.data || [], String(useAppStore().user?.uid ?? ''))
    }

    // Versione per liste: una sola lettura della tabella team → { id_farm_center: bool }
    async function getFarmCentersOwnerMap(ids) {
        const res = await dbStore.select('team')
        const rows = res?.ok ? res.data || [] : []
        const uid = String(useAppStore().user?.uid ?? '')
        const map = {}
        for (const id of ids) {
            map[id] = res?.ok ? _isOwnerFromTeamRows(rows.filter(r => r.id_farm_center === id), uid) : true
        }
        return map
    }

    async function deleteFarmCenter(id) {
        loading.value = true
        try {
            // Solo l'owner può eliminare (il server rifiuta comunque in /sync):
            // controllo prima di marcare _deleted, altrimenti il record resterebbe in retry infinito
            if (!(await isFarmCenterOwner(id))) {
                return { ok: false, message: t('onlyOwnerCanDelete') }
            }
            // Integrity check: plots and animals block deletion; seasons can be auto-removed if empty
            const [seasonsResult, plotsResult, animalsResult] = await Promise.all([
                dbStore.select('season', { id_farm_center: id }),
                dbStore.select('plot', { id_farm_center: id }),
                dbStore.select('animal', { id_farm_center: id })
            ])
            const deps = []
            const seasons = seasonsResult?.ok ? seasonsResult.data || [] : []
            const plotsCount = (plotsResult?.ok ? plotsResult.data || [] : []).length
            const animalsCount = (animalsResult?.ok ? animalsResult.data || [] : []).length
            if (plotsCount > 0) deps.push(`${plotsCount} appezzamento/i`)
            if (animalsCount > 0) deps.push(`${animalsCount} animale/i`)
            if (deps.length > 0) {
                return { ok: false, message: `Impossibile eliminare: ha ${deps.join(', ')}. Rimuovili prima.` }
            }

            // Se ci sono stagioni, verifico che siano vuote e le elimino in cascata
            for (const season of seasons) {
                const fieldsResult = await dbStore.select('field', { id_season: season.id_season })
                const fieldsCount = (fieldsResult?.ok ? fieldsResult.data || [] : []).length
                if (fieldsCount > 0) {
                    return { ok: false, message: `Impossibile eliminare: la stagione "${season.name || season.id_season}" ha ${fieldsCount} campo/i assegnato/i. Rimuovili prima.` }
                }
            }
            for (const season of seasons) {
                await dbStore.delete('season', season.id_season)
            }

            const result = await dbStore.delete('farm_center', id)
            if (result.ok) {
                if (currentFarmCenter.value?.id_farm_center === id) currentFarmCenter.value = null
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        } finally {
            loading.value = false
        }
    }

    async function deletePlot(id) {
        try {
            // Integrity check: no fields or plants
            const [fieldsResult, plantsResult] = await Promise.all([
                dbStore.select('field', { id_plot: id }),
                dbStore.select('plant', { id_plot: id })
            ])
            const deps = []
            const fieldsCount = (fieldsResult?.ok ? fieldsResult.data || [] : []).length
            const plantsCount = (plantsResult?.ok ? plantsResult.data || [] : []).length
            if (fieldsCount > 0) deps.push(`${fieldsCount} stagione/i`)
            if (plantsCount > 0) deps.push(`${plantsCount} pianta/e`)
            if (deps.length > 0) {
                return { ok: false, message: `Impossibile eliminare: ha ${deps.join(', ')}. Rimuovili prima.` }
            }

            const result = await dbStore.delete('plot', id)
            return result.ok ? { ok: true } : { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteSeason(id) {
        try {
            // Integrity check: no fields
            const fieldsResult = await dbStore.select('field', { id_season: id })
            const fieldsCount = (fieldsResult?.ok ? fieldsResult.data || [] : []).length
            if (fieldsCount > 0) {
                return { ok: false, message: `Impossibile eliminare: ha ${fieldsCount} stazione/i assegnato/i. Rimuovili prima.` }
            }

            const result = await dbStore.delete('season', id)
            if (result.ok) {
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function fetchCrops() {
        const result = await dbStore.select('crop')
        return result?.ok ? result.data || [] : []
    }

    // Ritorna opzioni colture ordinate per default desc, poi per name
    async function getCropOptions({ permanentOnly = false } = {}) {
        const all = await fetchCrops()
        return all
            .filter(c => !permanentOnly || c.permanent === 1 || c.permanent === true)
            .map(c => {
                const rawName = c.name || c.crop_name
                return { value: c.id_crop, label: translateCropName(rawName), _default: c.default ?? 0, _sortKey: rawName }
            })
            .sort((a, b) => b._default - a._default || a._sortKey.localeCompare(b._sortKey, 'it'))
            .map(({ _default, _sortKey, ...opt }) => opt)
    }

    // Fields actions (crop assignments to plot/season)
    async function fetchFieldsByFarmCenter(farmCenterId, seasonId = null) {
        try {
            // Costruisci il filtro per i fields
            const filter = { id_farm_center: farmCenterId }
            if (seasonId) {
                filter.id_season = seasonId
            }

            // Fai 3 query in parallelo: fields, crops, plots
            const [fieldsResult, cropsResult, plotsResult] = await Promise.all([
                dbStore.select('field', filter),
                dbStore.select('crop'),
                dbStore.select('plot', { id_farm_center: farmCenterId })
            ])

            if (fieldsResult.ok) {
                const fieldsData = fieldsResult.data || []
                const cropsData = cropsResult?.ok ? cropsResult.data || [] : []
                const plotsData = plotsResult?.ok ? plotsResult.data || [] : []

                // Crea mappe per accesso rapido
                const cropsMap = {}
                cropsData.forEach(crop => {
                    cropsMap[crop.id_crop] = crop
                })

                const plotsMap = {}
                plotsData.forEach(plot => {
                    plotsMap[plot.id_plot] = plot
                })

                // Arricchisci ogni field con i dati di crop e plot
                const enrichedFields = fieldsData.map(field => {
                    const enrichedField = { ...field }

                    if (field.id_crop && cropsMap[field.id_crop]) {
                        enrichedField.crop = cropsMap[field.id_crop]
                    }

                    if (field.id_plot && plotsMap[field.id_plot]) {
                        enrichedField.plot = plotsMap[field.id_plot]

                        if (!enrichedField.name && plotsMap[field.id_plot].name) {
                            enrichedField.name = plotsMap[field.id_plot].name
                        }
                    }

                    return enrichedField
                })

                return enrichedFields
            }
            return []
        } catch (e) {
            console.error('Error fetching fields:', e)
            return []
        }
    }

    /**
     * Restituisce i plot con i relativi fields/colture per una stagione specifica
     * @param {string|number} farmCenterId - ID del farm center
     * @param {string|number} seasonId - ID della stagione
     * @returns {Promise<Array>} Array di plot arricchiti con fields e crops
     */
    async function getPlotFields(farmCenterId, seasonId) {
        try {
            // Query parallele: plots, fields per quella stagione, crops
            const [plotsResult, fieldsResult, cropsResult] = await Promise.all([
                dbStore.select('plot', { id_farm_center: farmCenterId }),
                dbStore.select('field', { id_farm_center: farmCenterId, id_season: seasonId }),
                dbStore.select('crop')
            ])

            const plotsData = plotsResult?.ok ? plotsResult.data || [] : []
            const fieldsData = fieldsResult?.ok ? fieldsResult.data || [] : []
            const cropsData = cropsResult?.ok ? cropsResult.data || [] : []

            // Crea mappa crops per accesso rapido
            const cropsMap = {}
            cropsData.forEach(crop => {
                cropsMap[crop.id_crop] = crop
            })

            // Crea mappa fields raggruppati per id_plot
            const fieldsByPlot = {}
            fieldsData.forEach(field => {
                if (!fieldsByPlot[field.id_plot]) {
                    fieldsByPlot[field.id_plot] = []
                }
                
                // Arricchisci field con crop
                const enrichedField = { ...field }
                if (field.id_crop && cropsMap[field.id_crop]) {
                    enrichedField.crop = cropsMap[field.id_crop]
                }
                
                fieldsByPlot[field.id_plot].push(enrichedField)
            })

            // Arricchisci ogni plot con i suoi fields
            const enrichedPlots = plotsData.map(plot => {
                return {
                    ...plot,
                    fields: fieldsByPlot[plot.id_plot] || [] // Array vuoto se non ci sono fields
                }
            })

            return enrichedPlots
        } catch (e) {
            console.error('Error fetching plot fields:', e)
            return []
        }
    }

    async function createField(data) {
        try {
            const result = await dbStore.insert('field', data)
            if (result.ok) {
                return { ok: true, data: result.data }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function createFields(fieldsArray) {
        try {
            const results = await Promise.all(
                fieldsArray.map(data => dbStore.insert('field', data))
            )
            const created = results.filter(r => r.ok).map(r => r.data)
            return { ok: true, count: created.length, total: fieldsArray.length }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function updateField(id, changes) {
        try {
            const result = await dbStore.update('field', id, changes)
            if (result.ok) {
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteField(id) {
        try {
            // Integrity check: no operations or observations referencing this field
            const [opsResult, obsResult] = await Promise.all([
                dbStore.select('operation', { id_field: id }),
                dbStore.select('observation', { id_field: id })
            ])
            const deps = []
            const opsCount = (opsResult?.ok ? opsResult.data || [] : []).length
            const obsCount = (obsResult?.ok ? obsResult.data || [] : []).length
            if (opsCount > 0) deps.push(`${opsCount} operazione/i`)
            if (obsCount > 0) deps.push(`${obsCount} osservazione/i`)
            if (deps.length > 0) {
                return { ok: false, message: `Impossibile eliminare: ha ${deps.join(', ')}. Rimuovili prima.` }
            }

            const result = await dbStore.delete('field', id)
            if (result.ok) {
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteAnimal(id) {
        try {
            // Integrity check: no events
            const eventsResult = await dbStore.select('event', { id_animal: id })
            const eventsCount = (eventsResult?.ok ? eventsResult.data || [] : []).length
            if (eventsCount > 0) {
                return { ok: false, message: `Impossibile eliminare: ha ${eventsCount} evento/i. Rimuovili prima.` }
            }

            const result = await dbStore.delete('animal', id)
            if (result.ok) {
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteGeoElement(id) {
        try {
            // Integrity check: no survey_geo
            const surveyResult = await dbStore.select('survey_geo', { id_geo_element: id })
            const surveyCount = (surveyResult?.ok ? surveyResult.data || [] : []).length
            if (surveyCount > 0) {
                return { ok: false, message: `Impossibile eliminare: ha ${surveyCount} rilevamento/i. Rimuovili prima.` }
            }

            const result = await dbStore.delete('geo_element', id)
            if (result.ok) {
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    // Plant management (batch operations for orchards)
    async function createPlants(plantsArray) {
        try {
            // Batch insert: process in chunks to avoid overwhelming the store
            const BATCH_SIZE = 100
            let totalCreated = 0
            let firstError = null
            for (let i = 0; i < plantsArray.length; i += BATCH_SIZE) {
                const chunk = plantsArray.slice(i, i + BATCH_SIZE)
                const results = await Promise.all(
                    chunk.map(data => dbStore.insert('plant', data))
                )
                totalCreated += results.filter(r => r.ok).length
                if (!firstError) firstError = results.find(r => !r.ok)?.message || null
            }
            return { ok: totalCreated === plantsArray.length, count: totalCreated, total: plantsArray.length, message: firstError }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deletePlantsByPlot(plotId) {
        try {
            const plantsResult = await dbStore.select('plant', { id_plot: plotId })
            const plants = plantsResult?.ok ? plantsResult.data || [] : []
            if (plants.length === 0) return { ok: true, count: 0 }

            // Batch delete
            const BATCH_SIZE = 100
            let totalDeleted = 0
            for (let i = 0; i < plants.length; i += BATCH_SIZE) {
                const chunk = plants.slice(i, i + BATCH_SIZE)
                const results = await Promise.all(
                    chunk.map(p => dbStore.delete('plant', p.id_plant))
                )
                totalDeleted += results.filter(r => r.ok).length
            }
            return { ok: true, count: totalDeleted }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function fetchPlantsByPlot(plotId) {
        try {
            const result = await dbStore.select('plant', { id_plot: plotId })
            return result?.ok ? result.data || [] : []
        } catch (e) {
            console.error('Error fetching plants:', e)
            return []
        }
    }

    // Accession & Variety helpers
    async function fetchVarieties() {
        try {
            const result = await dbStore.select('variety')
            return result?.ok ? result.data || [] : []
        } catch (e) {
            console.error('Error fetching varieties:', e)
            return []
        }
    }

    async function fetchAccessions() {
        try {
            const result = await dbStore.select('accession')
            return result?.ok ? result.data || [] : []
        } catch (e) {
            console.error('Error fetching accessions:', e)
            return []
        }
    }

    async function checkExcelAccessions(farmCenterId, plotId, file, upsert = false) {
        const formData = new FormData()
        formData.append('file', file)
        try {
            const url = `${BASE_URL}/api/v2/poderi2/${farmCenterId}/plot/${plotId}/check_file_excel${upsert ? '?upsert=1' : ''}`
            const resp = await fetch(url, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            })
            return await resp.json()
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function generateAccessionTemplate(farmCenterId, plotId) {
        try {
            const resp = await fetch(`${BASE_URL}/api/v2/poderi2/${farmCenterId}/plot/${plotId}/accession_template`, {
                method: 'GET',
                credentials: 'include'
            })
            return await resp.json()
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function uploadExcelAccessions(farmCenterId, plotId, file, upsert = false) {
        const formData = new FormData()
        formData.append('file', file)
        const url = `${BASE_URL}/api/v2/poderi2/${farmCenterId}/plot/${plotId}/upload_file_excel${upsert ? '?upsert=1' : ''}`
        try {
            const resp = await fetch(url, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            })
            return await resp.json()
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function resetAccessions(farmCenterId, plotId) {
        try {
            const resp = await fetch(`${BASE_URL}/api/v2/poderi2/${farmCenterId}/plot/${plotId}/reset_accessions`, {
                method: 'POST',
                credentials: 'include'
            })
            return await resp.json()
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    /**
     * Sincronizza i dati dal backend nel DB locale (Dexie) dopo un upload accession.
     * @param {Object} syncData - { plants: [...], accessions: [...], varieties: [...] }
     * @param {string} plotId - id del plot (per filtrare le piante da sostituire)
     */
    async function syncAccessionData(syncData, plotId) {
        if (!syncData) return

        // Aggiorna piante: rimuovi quelle del plot e reinserisci
        if (syncData.plants?.length > 0) {
            try {
                const table = dbStore.db.table('plant')
                // Rimuovi piante esistenti per questo plot (id_plot non è indicizzato in Dexie)
                const all = await table.toArray()
                const existing = all.filter(p => p.id_plot === plotId)
                if (existing.length > 0) {
                    await table.bulkDelete(existing.map(p => p.id_plant))
                }
                await table.bulkPut(syncData.plants)
            } catch (e) {
                console.warn('Errore sync piante:', e)
            }
        }

        // Aggiorna accession: svuota e reinserisci tutte (sono org-scoped)
        if (syncData.accessions) {
            try {
                const table = dbStore.db.table('accession')
                await table.clear()
                if (syncData.accessions.length > 0) {
                    await table.bulkPut(syncData.accessions)
                }
            } catch (e) {
                console.warn('Errore sync accession:', e)
            }
        }

        // Aggiorna varietà: svuota e reinserisci tutte (sono generali)
        if (syncData.varieties) {
            try {
                const table = dbStore.db.table('variety')
                await table.clear()
                if (syncData.varieties.length > 0) {
                    await table.bulkPut(syncData.varieties)
                }
            } catch (e) {
                console.warn('Errore sync varietà:', e)
            }
        }
    }

    // Survey plant management (batch upsert for plant surveys)
    async function upsertSurveyPlant(surveysArray) {
        try {
            // Batch upsert: process in chunks
            const BATCH_SIZE = 100
            let totalUpserted = 0
            
            for (let i = 0; i < surveysArray.length; i += BATCH_SIZE) {
                const chunk = surveysArray.slice(i, i + BATCH_SIZE)
                const results = await Promise.all(
                    chunk.map(async (survey) => {
                        // Check if survey exists (has id_survey_plant and is not a new GUID)
                        const existing = await dbStore.selectOne('survey_plant', survey.id_survey_plant)
                        
                        if (existing.ok && existing.data) {
                            // Update existing survey
                            return dbStore.update('survey_plant', survey.id_survey_plant, survey)
                        } else {
                            // Insert new survey
                            return dbStore.insert('survey_plant', survey)
                        }
                    })
                )
                totalUpserted += results.filter(r => r.ok).length
            }
            
            return { ok: true, count: totalUpserted, total: surveysArray.length }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function fetchSurveysByPlot(plotId, schemaId = null, enrich = false) {
        try {
            // Carica sempre le piante del plot: è la fonte di verità per il filtro,
            // più affidabile di id_plot sul survey (campo assente nei record legacy)
            const [plantsResult, allSurveysResult] = await Promise.all([
                dbStore.select('plant', { id_plot: plotId }),
                dbStore.select('survey_plant')
            ])

            const plantMap = new Map()
            plantsResult.data?.forEach(p => plantMap.set(p.id_plant, p))

            const plantIds = new Set(plantMap.keys())

            let surveys = (allSurveysResult?.ok ? allSurveysResult.data || [] : [])
                .filter(s => plantIds.has(s.id_plant))

            if (schemaId) {
                surveys = surveys.filter(s => s.id_survey_schema === schemaId)
            }

            if (!enrich || surveys.length === 0) return surveys

            // Enrich: plant → accession → variety
            const [accessionsResult, varietiesResult] = await Promise.all([
                dbStore.select('accession'),
                dbStore.select('variety')
            ])

            const accessionMap = new Map()
            accessionsResult.data?.forEach(a => accessionMap.set(a.id_accession, a))

            const varietyMap = new Map()
            varietiesResult.data?.forEach(v => varietyMap.set(v.id_variety, v))

            return surveys.map(survey => {
                const plant = plantMap.get(survey.id_plant)
                const accession = plant ? accessionMap.get(plant.id_accession) : null
                const variety = accession ? varietyMap.get(accession.id_variety) : null
                return {
                    ...survey,
                    row_code: plant?.row_code ?? null,
                    col_code: plant?.col_code ?? null,
                    _plant_code_tag: plant?.plant_code ?? null,
                    accession_code: accession?.accession_code ?? null,
                    variety_name: variety?.name ?? null
                }
            })
        } catch (e) {
            console.error('[poderiStore] fetchSurveysByPlot error:', e)
            return []
        }
    }

    // Operations and Observations actions
    async function fetchOperationsAndObservations(farmCenterId) {
        try {
            // Fai 7 query in parallelo: operations, observations, crop_operation types, survey_schemas, fields, plots, crops
            const [opsResult, obsResult, cropOpsResult, schemasResult, fieldsResult, plotsResult, cropsResult, trapsResult] = await Promise.all([
                dbStore.select('operation', { id_farm_center: farmCenterId }),
                dbStore.select('observation', { id_farm_center: farmCenterId }),
                dbStore.select('crop_operation', { active: 1 }),
                dbStore.select('survey_schema', { active: 1 }),
                dbStore.select('field', { id_farm_center: farmCenterId }),
                dbStore.select('plot', { id_farm_center: farmCenterId }),
                dbStore.select('crop'),
                dbStore.select('trap', { id_farm_center: farmCenterId }),
            ])

            const operationsData = opsResult?.ok ? opsResult.data || [] : []
            const observationsData = obsResult?.ok ? obsResult.data || [] : []
            const cropOperationTypes = cropOpsResult?.ok ? cropOpsResult.data || [] : []
            const surveySchemas = schemasResult?.ok ? schemasResult.data || [] : []
            const fieldsData = fieldsResult?.ok ? fieldsResult.data || [] : []
            const plotsData = plotsResult?.ok ? plotsResult.data || [] : []
            const cropsData = cropsResult?.ok ? cropsResult.data || [] : []
            const trapsData = trapsResult?.ok ? trapsResult.data || [] : []

            // Crea mappa plots per accesso rapido
            const plotsMap = {}
            plotsData.forEach(plot => {
                plotsMap[plot.id_plot] = plot
            })

            // Crea mappa crops per accesso rapido
            const cropsMap = {}
            cropsData.forEach(crop => {
                cropsMap[crop.id_crop] = crop
            })

            // Crea mappe per accesso rapido
            const fieldsMap = {}
            fieldsData.forEach(field => {
                // Arricchisci field con plot name e crop name
                const enrichedField = { ...field }
                if (field.id_plot && plotsMap[field.id_plot]) {
                    if (!enrichedField.name && plotsMap[field.id_plot].name) {
                        enrichedField.name = plotsMap[field.id_plot].name
                    }
                }
                if (field.id_crop && cropsMap[field.id_crop]) {
                    enrichedField.crop_name = cropsMap[field.id_crop].name || cropsMap[field.id_crop].crop_name || ''
                }
                fieldsMap[field.id_field] = enrichedField
            })

            const cropOpsMap = {}
            cropOperationTypes.forEach(op => {
                cropOpsMap[op.id_crop_operation] = op
            })

            const schemasMap = {}
            surveySchemas.forEach(schema => {
                schemasMap[schema.id_survey_schema] = schema
            })

            const trapsMap = {}
            trapsData.forEach(trap => {
                trapsMap[trap.id_trap] = trap
            })

            // Helper: risolvi field_names e crop_names da id_fields
            function resolveFieldInfo(item) {
                const ids = item.id_fields && Array.isArray(item.id_fields)
                    ? item.id_fields
                    : item.id_field ? [item.id_field] : []

                const fieldNames = ids.map(id => fieldsMap[id]?.name || 'N/D')
                const cropNamesSet = new Set()
                ids.forEach(id => {
                    const cn = fieldsMap[id]?.crop_name
                    if (cn) cropNamesSet.add(cn)
                })

                return {
                    field_names: fieldNames.length > 0 ? fieldNames : ['N/D'],
                    crop_names: [...cropNamesSet]
                }
            }

            // Arricchisci operations
            operations.value = operationsData.map(op => {
                const enriched = { ...op }
                const { field_names, crop_names } = resolveFieldInfo(op)
                enriched.field_names = field_names
                enriched.crop_names = crop_names

                // Aggiungi operation type name e schema
                const opType = cropOpsMap[op.id_crop_operation]
                enriched.operation_type_name = opType?.name || null
                enriched._schema = opType?.schema || []

                return enriched
            })

            // Arricchisci observations
            observations.value = observationsData.map(obs => {
                const enriched = { ...obs }
                const { field_names, crop_names } = resolveFieldInfo(obs)
                enriched.field_names = field_names
                enriched.crop_names = crop_names

                // Aggiungi observation type name e schema
                const obsType = schemasMap[obs.id_survey_schema]
                enriched.observation_type_name = obsType?.name || null
                enriched._schema = obsType?.schema || []

                // Arricchisci con dati trappola se presente
                if (obs.id_trap && trapsMap[obs.id_trap]) {
                    const trap = trapsMap[obs.id_trap]
                    enriched.trap_name = trap.name || null
                    enriched.trap_pest = trap.pest || null
                    enriched.trap_type = trap.type || null
                }

                return enriched
            })
        } catch (e) {
            console.error('Error fetching operations and observations:', e)
        }
    }

    async function getAllFields() {
        try {
            const [fieldsResult, seasonsResult, cropsResult, plotsResult, farmCentersResult] = await Promise.all([
                dbStore.select('field'),
                dbStore.select('season'),
                dbStore.select('crop'),
                dbStore.select('plot'),
                dbStore.select('farm_center')
            ])

            const fieldsData = fieldsResult?.ok ? fieldsResult.data || [] : []
            const seasonsData = seasonsResult?.ok ? seasonsResult.data || [] : []
            const cropsData = cropsResult?.ok ? cropsResult.data || [] : []
            const plotsData = plotsResult?.ok ? plotsResult.data || [] : []
            const farmCentersData = farmCentersResult?.ok ? farmCentersResult.data || [] : []

            const seasonsMap = {}
            seasonsData.forEach(s => { seasonsMap[s.id_season] = s })

            const cropsMap = {}
            cropsData.forEach(c => { cropsMap[c.id_crop] = c })

            const plotsMap = {}
            plotsData.forEach(p => { plotsMap[p.id_plot] = p })

            const farmCentersMap = {}
            farmCentersData.forEach(fc => { farmCentersMap[fc.id_farm_center] = fc })

            allFields.value = fieldsData.map(field => {
                const season = seasonsMap[field.id_season]
                const crop = cropsMap[field.id_crop]
                const plot = plotsMap[field.id_plot]
                const farmCenter = farmCentersMap[field.id_farm_center]

                const year = season?.date_from
                    ? new Date(season.date_from).getFullYear()
                    : season?.date_to
                        ? new Date(season.date_to).getFullYear()
                        : null

                return {
                    ...field,
                    year,
                    season_name: season?.season_name || '',
                    crop_name: crop?.name || crop?.crop_name || 'N/D',
                    plot_name: plot?.name || field.name || 'N/D',
                    farm_name: farmCenter?.name || '',
                    area: plot?.area || plot?.surface || 0,
                    lat: plot?.lat || null,
                    lon: plot?.lon || null
                }
            })

            return allFields.value
        } catch (e) {
            console.error('Error fetching all fields:', e)
            return []
        }
    }

    // Warehouse / Product management
    async function fetchProductsByFarmCenter(farmCenterId) {
        try {
            const result = await dbStore.select('product', { id_farm_center: farmCenterId })
            if (result.ok) {
                products.value = result.data || []
            }
        } catch (e) {
            console.error('Error fetching products:', e)
        }
    }

    async function createProduct(data) {
        try {
            const result = await dbStore.insert('product', data)
            if (result.ok) {
                return { ok: true, data: result.data }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function updateProduct(id, data) {
        try {
            const result = await dbStore.update('product', id, data)
            if (result.ok) {
                const index = products.value.findIndex(p => p.id_product === id)
                if (index !== -1) products.value[index] = { ...products.value[index], ...data }
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteProduct(id) {
        try {
            const result = await dbStore.delete('product', id)
            if (result.ok) {
                products.value = products.value.filter(p => p.id_product !== id)
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteOperation(id) {
        try {
            const result = await dbStore.delete('operation', id)
            if (result.ok) {
                operations.value = operations.value.filter(o => o.id_operation !== id)
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function deleteObservation(id) {
        try {
            const result = await dbStore.delete('observation', id)
            if (result.ok) {
                observations.value = observations.value.filter(o => o.id_observation !== id)
                return { ok: true }
            }
            return { ok: false, message: result.message }
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    // ===== MARKETING =====

    async function publishMarketing(idFarmCenter) {
        try {
            const result = await api.post(`/api/v2/poderi2/marketing/publish/${idFarmCenter}`)
            return result
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function unpublishMarketing(idFarmCenter) {
        try {
            const result = await api.post(`/api/v2/poderi2/marketing/unpublish/${idFarmCenter}`)
            return result
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    async function getMarketingStatus(idFarmCenter) {
        try {
            const result = await api.get(`/api/v2/poderi2/marketing/status/${idFarmCenter}`)
            return result
        } catch (e) {
            return { ok: false, message: e.message }
        }
    }

    function clearCurrentFarmCenter() {
        currentFarmCenter.value = null
        currentSeasonId.value = null
        operations.value = []
        observations.value = []
        products.value = []
    }

    function clearError() {
        error.value = null
    }

    // === Helper per i modelli agronomici ===
    // Tutti async: fanno query via dbStore perché gli array reattivi dello store
    // (crops, fields, plots, seasons) NON sono necessariamente popolati
    // quando si entra in FieldDetail (che carica i dati via dbStore.selectOne)

    async function getCrop(idCrop) {
        if (!idCrop) return null
        
        const result = await dbStore.selectOne('crop', idCrop)
        return result.ok ? result.data : null
    }

    async function getFieldById(idField) {
        if (!idField) return null
        const result = await dbStore.selectOne('field', idField)
        return result.ok ? result.data : null
    }

    async function getPlotById(idPlot) {
        if (!idPlot) return null
        const result = await dbStore.selectOne('plot', idPlot)
        return result.ok ? result.data : null
    }

    async function getSeasonById(idSeason) {
        if (!idSeason) return null
        const result = await dbStore.selectOne('season', idSeason)
        return result.ok ? result.data : null
    }

    async function getPreviousSeason(idFarmCenter, idSeason) {
        // Carica tutte le stagioni del farm center
        const result = await dbStore.select('season', { id_farm_center: idFarmCenter })
        if (!result.ok) return null
        const allSeasons = result.data || []

        const currentSeason = allSeasons.find(s => s.id_season === idSeason)
        if (!currentSeason) return null

        const sorted = allSeasons
            .filter(s => s.id_season !== idSeason)
            .sort((a, b) => (b.date_from || '').localeCompare(a.date_from || ''))

        return sorted.find(s => (s.date_from || '') < (currentSeason.date_from || '')) || null
    }

    function getOperationsByField(idField) {
        return operations.value.filter(op => {
            if (op.id_fields && Array.isArray(op.id_fields)) {
                return op.id_fields.includes(idField)
            }
            return op.id_field === idField
        })
    }

    function getObservationsByField(idField) {
        return observations.value.filter(obs => {
            if (obs.id_fields && Array.isArray(obs.id_fields)) {
                return obs.id_fields.includes(idField)
            }
            return obs.id_field === idField
        })
    }

    async function updateAccession(farmCenterId, idAccession, data) {
        return api.put(`/api/v2/poderi2/${farmCenterId}/accession/${idAccession}`, data)
    }

    async function updatePlant(farmCenterId, idPlant, data) {
        return api.put(`/api/v2/poderi2/${farmCenterId}/plant/${idPlant}`, data)
    }

    return {
        currentFarmCenter,
        currentSeasonId,
        operations,
        observations,
        allFields,
        products,
        loading,
        error,
        fetchFarmCenters,
        fetchFarmCenterById,
        fetchPlotsByFarmCenter,
        fetchSeasonsByFarmCenter,
        fetchSoils,
        getSoil,
        createSoil,
        updateSoil,
        deleteSoil,
        enrichPlot,
        checkPlotsNeedingUpdate,
        getPlotEnrichmentData,
        fetchAllPlots,
        fetchAllSeasons,
        fetchCrops,
        getCropOptions,
        fetchFieldsByFarmCenter,
        getPlotFields,
        fetchOperationsAndObservations,
        createFarmCenter,
        updateFarmCenter,
        deleteFarmCenter,
        isFarmCenterOwner,
        getFarmCentersOwnerMap,
        deletePlot,
        deleteSeason,
        createField,
        createFields,
        updateField,
        deleteField,
        deleteAnimal,
        deleteGeoElement,
        createPlants,
        deletePlantsByPlot,
        fetchPlantsByPlot,
        fetchVarieties,
        fetchAccessions,
        checkExcelAccessions,
        uploadExcelAccessions,
        generateAccessionTemplate,
        resetAccessions,
        syncAccessionData,
        upsertSurveyPlant,
        fetchSurveysByPlot,
        getAllFields,
        fetchProductsByFarmCenter,
        createProduct,
        updateProduct,
        deleteProduct,
        deleteOperation,
        deleteObservation,
        publishMarketing,
        unpublishMarketing,
        getMarketingStatus,
        clearCurrentFarmCenter,
        clearError,
        getCrop,
        getFieldById,
        getPlotById,
        getSeasonById,
        getPreviousSeason,
        getOperationsByField,
        getObservationsByField,
        updateAccession,
        updatePlant
    }
})
